import { useState, useEffect } from "react";
import { getFeedbacks } from "./supportApi";
import { toast } from "react-toastify";

export default function FeedbackManagement() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchFeedbacks();
  }, [page, limit]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const data = await getFeedbacks(page, limit);
      setFeedbacks(data.feedback || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
      toast.error("Error fetching feedbacks");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US");
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-base ${
              star <= rating ? "text-yellow-400" : "text-muted-foreground/30"
            }`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Feedback <span className="text-gradient-ginox">Management</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          Manage user feedback and ratings
        </p>
      </div>

      {/* Card */}
      <div className="rounded-lg border border-border/70 bg-card">
        {/* Card header */}
        <div className="mb-0 px-5 pt-5 pb-4 border-b border-border/70">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              All Feedbacks ({total})
            </p>
            <div className="flex items-center gap-3">
              <label className="text-xs text-muted-foreground">
                Items per page:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 md:w-auto"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Card body */}
        <div className="p-5">
          {loading ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green mx-auto" />
            </div>
          ) : feedbacks.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              No data found
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0 min-w-[600px]">
                <thead>
                  <tr>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Username
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Rating
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Feedback
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks.map((feedback) => (
                    <tr
                      key={feedback.id}
                      className="hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="border-b border-border/40 px-4 py-3 text-sm font-medium text-foreground whitespace-nowrap">
                        {feedback.username || "N/A"}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 whitespace-nowrap">
                        {renderStars(feedback.rating || 0)}
                      </td>
                      <td
                        className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground max-w-xs truncate"
                        title={feedback.feedback}
                      >
                        {feedback.feedback || "No feedback"}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm font-mono text-muted-foreground whitespace-nowrap">
                        {formatDate(feedback.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col md:flex-row justify-center items-center gap-3 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
